/**
 * `RuntimeState` — the complete, serializable, replayable session state
 * (architecture.md §3.2).
 *
 * @remarks
 * Eight cohesive sub-states (D241 added the run's view of the Record), grouped
 * from day one because save migration is
 * coupled to this shape. The §3.2 invariants this schema enforces or serves:
 * JSON round-trip loses nothing (every field is plain data — no class
 * instances, functions, `Map`/`Set`, or `Date`); transition functions return
 * fresh states (parsed output is deep-frozen, so accidental mutation throws
 * in strict mode); no derived data (anything computable from state + bundle
 * lives in selectors). Saves and replays validate against this schema on
 * load.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { mapDiscoveryStateSchema } from './map-discovery-state.js';
import { narrativeRuntimeStateSchema } from './narrative-runtime-state.js';
import { progressionStateSchema } from './progression-state.js';
import { randomnessStateSchema } from './randomness-state.js';
import { recordViewStateSchema } from './record-view-state.js';
import { schedulerStateSchema } from './scheduler-state.js';
import { sessionStateSchema } from './session-state.js';
import { worldStateSchema } from './world-state.js';

/** The complete session state — eight sub-states, all serializable, all replayable. */
export const runtimeStateSchema = z
	.strictObject({
		mapDiscovery: mapDiscoveryStateSchema,
		narrative: narrativeRuntimeStateSchema,
		progression: progressionStateSchema,
		randomness: randomnessStateSchema,
		record: recordViewStateSchema,
		scheduler: schedulerStateSchema,
		session: sessionStateSchema,
		world: worldStateSchema,
	})
	.readonly();

/** The complete session state — see {@link runtimeStateSchema}. */
export type RuntimeState = z.infer<typeof runtimeStateSchema>;
